import { TriangleAlert, Trophy, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { MultiCombobox } from '@components/combobox';
import { Badge } from '@components/ui/badge';
import { cn } from '@core/utils/cn';
import { injectTSDI } from '../../../core/tsdi';
import { Api } from '../../../core/api';

export interface BadgeTypeOption {
    id: number;
    name: Record<string, string>;
    categoryName: Record<string, string>;
    goalId: number | null;
    hasGoal: boolean;
}

interface BadgeTypeMultiSelectProps {
    value: BadgeTypeOption[];
    onChange: (badgeTypes: BadgeTypeOption[]) => void;
    label?: string;
}

const getLocalizedName = (name: Record<string, string> | undefined): string => {
    if (!name) return 'Unnamed';
    return name['en'] || name[Object.keys(name)[0]] || 'Unnamed';
};

export const BadgeTypeMultiSelect: React.FC<BadgeTypeMultiSelectProps> = ({
    value,
    onChange,
    label = 'Select Badge Types'
}) => {
    const [options, setOptions] = useState<BadgeTypeOption[]>([]);
    const [loading, setLoading] = useState(false);
    const { fitnessActivityClient } = injectTSDI(Api);

    useEffect(() => {
        loadBadgeTypes();
    }, []);

    const loadBadgeTypes = async () => {
        setLoading(true);
        try {
            const response =
                await fitnessActivityClient.admin.badgeTypeAdminControllerGetTypes(
                    { size: 500 }
                );

            if (response?.data?.content) {
                const badgeTypeOptions: BadgeTypeOption[] =
                    response.data.content.map((type: any) => ({
                        id: type.id,
                        name: type.name,
                        categoryName: type.categoryName || {},
                        goalId: type.goalId ?? null,
                        hasGoal: type.goalId != null
                    }));

                setOptions(badgeTypeOptions);
            }
        } catch (error) {
            console.error('Failed to load badge types:', error);
        } finally {
            setLoading(false);
        }
    };

    const sortedOptions = [...options].sort((a, b) => {
        if (a.hasGoal !== b.hasGoal) return a.hasGoal ? -1 : 1;
        return getLocalizedName(a.categoryName).localeCompare(
            getLocalizedName(b.categoryName)
        );
    });

    return (
        <MultiCombobox
            label={label}
            placeholder="Search badge types..."
            searchPlaceholder="Search badge types..."
            options={sortedOptions}
            value={value}
            onChange={onChange}
            loading={loading}
            getOptionKey={(option) => option.id}
            getOptionLabel={(option) =>
                `${getLocalizedName(option.categoryName)} - ${getLocalizedName(option.name)}`
            }
            helperText={
                value.some((v) => !v.hasGoal)
                    ? 'Warning: Some selected badge types have no linked goal'
                    : undefined
            }
            renderOption={(option) => (
                <div className="flex w-full items-center gap-2">
                    <Trophy
                        className={cn(
                            'size-5',
                            option.hasGoal
                                ? 'text-warning'
                                : 'text-muted-foreground/50'
                        )}
                    />
                    <div className="grow">
                        <p className="text-sm">
                            {getLocalizedName(option.name)}
                        </p>
                        <span className="text-xs text-muted-foreground">
                            {getLocalizedName(option.categoryName)}
                        </span>
                    </div>
                    {!option.hasGoal && (
                        <TriangleAlert
                            className="size-4 text-warning"
                            aria-label="No goal linked - cannot be assigned"
                        />
                    )}
                </div>
            )}
            renderChip={(option, remove) => (
                <Badge
                    variant={option.hasGoal ? 'secondary' : 'warning'}
                    className="gap-1"
                >
                    <Trophy />
                    {getLocalizedName(option.name)}
                    <X
                        className="size-3 cursor-pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            remove();
                        }}
                    />
                </Badge>
            )}
        />
    );
};
